import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  useTheme,
  alpha,
  Zoom,
  Button,
  Divider,
} from '@mui/material';
import { Close, CheckCircleOutline } from '@mui/icons-material';
import EnquiryForm from './EnquiryForm';

const PopupEnquiryModal = ({ open, onClose, initialSuccessData = null, onFormSubmitted = null }) => {
  const theme = useTheme();
  const [submitted, setSubmitted] = useState(false);
  const [submittedDetails, setSubmittedDetails] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      if (initialSuccessData) {
        // Open directly in success mode with provided data
        setSubmittedDetails(initialSuccessData);
        setSubmitted(true);
      } else {
        setSubmitted(false);
        setSubmittedDetails(null);
      }
    }
  }, [open, initialSuccessData]);

  const handleSuccess = (values) => {
    setSubmittedDetails(values);
    setSubmitted(true);
    if (onFormSubmitted) {
      onFormSubmitted(values);
    }
  };

  const detailFields = [
    { label: 'Full Name', value: submittedDetails?.studentName },
    { label: 'Contact Number', value: submittedDetails?.contactNo },
    { label: 'Email Address', value: submittedDetails?.email },
    { label: 'Course Interested In', value: submittedDetails?.courseInterestedIn },
    { label: 'Message', value: submittedDetails?.message },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={false}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Zoom}
      transitionDuration={400}
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 2 },
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          mx: { xs: 2, sm: 0 },
          my: { xs: 4, sm: 0 },
          maxHeight: { xs: 'calc(100vh - 64px)', sm: 'none' },
          height: { xs: 'auto', sm: 'auto' },
        },
      }}
    >
      {/* Gradient Header Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: { xs: 110, sm: 160 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          borderBottomLeftRadius: { xs: 16, sm: 24 },
          borderBottomRightRadius: { xs: 16, sm: 24 },
        }}
      />

      {/* Close Button */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          zIndex: 2,
          bgcolor: 'rgba(255,255,255,0.2)',
          color: 'white',
          backdropFilter: 'blur(4px)',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.35)',
            transform: 'rotate(90deg)',
          },
        }}
      >
        <Close fontSize="small" />
      </IconButton>

      {/* Decorative Circles */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.08)',
          zIndex: 0,
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {!submitted ? (
        <>
          <DialogTitle sx={{ pb: 0, pt: { xs: 2.5, sm: 4 }, position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              {/* Icon Badge */}
              <Box
                sx={{
                  width: { xs: 56, sm: 70 },
                  height: { xs: 56, sm: 70 },
                  borderRadius: '50%',
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
              >
                <Typography variant="h4" sx={{ lineHeight: 1 }}>
                  🎓
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: { xs: '1.2rem', sm: '1.8rem' },
                  textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                Get Free Counselling
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  mt: 0.5,
                  maxWidth: 320,
                  mx: 'auto',
                }}
              >
                Fill in your details and our experts will get back to you within 24 hours
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 1.5, sm: 4 }, pb: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, position: 'relative', zIndex: 1 }}>
            {/* Trust Badges */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 0.5, sm: 3 },
                mb: 2.5,
                flexWrap: 'wrap',
              }}
            >
              {[
                { icon: '🔒', label: '100% Secure' },
                { icon: '⚡', label: 'Instant Reply' },
                { icon: '✅', label: 'Free Service' },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    px: { xs: 1, sm: 1.5 },
                    py: 0.4,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1 }}>
                    {item.icon}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'success.dark',
                      fontSize: '0.7rem',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <EnquiryForm variant="popup" onSuccess={handleSuccess} />
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle sx={{ pb: 0, pt: { xs: 2.5, sm: 4 }, position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              {/* Success Icon */}
              <Box
                sx={{
                  width: { xs: 64, sm: 80 },
                  height: { xs: 64, sm: 80 },
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  animation: 'pulse 1.5s ease-in-out',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(0.8)', opacity: 0 },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              >
                <CheckCircleOutline
                  sx={{
                    fontSize: { xs: 36, sm: 48 },
                    color: 'success.main',
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: { xs: '1.1rem', sm: '1.6rem' },
                  textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                Enquiry Submitted Successfully!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  mt: 0.5,
                  maxWidth: 320,
                  mx: 'auto',
                }}
              >
                Thank you for your enquiry. Our team will review your details and get back to you within 24 hours.
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 1.5, sm: 4 }, pb: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, position: 'relative', zIndex: 1 }}>
            {/* Submitted Details Card */}
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                mb: 3,
                mt: 1,
              }}
            >
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.06),
                  px: 3,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: 'success.dark' }}
                >
                  📋 Your Submitted Details
                </Typography>
              </Box>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
                {detailFields.map((field, index) => {
                  // Skip message field if empty
                  if (field.label === 'Message' && !field.value) return null;
                  return (
                    <Box key={field.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'flex-start' },
                          py: { xs: 0.75, sm: 1 },
                          gap: { xs: 0.25, sm: 0 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            minWidth: 120,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          }}
                        >
                          {field.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            textAlign: { xs: 'left', sm: 'right' },
                            flex: 1,
                            ml: { xs: 0, sm: 2 },
                            wordBreak: 'break-word',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          }}
                        >
                          {field.label === 'Course Interested In'
                            ? field.value
                            : field.value}
                        </Typography>
                      </Box>
                      {/* Divider between rows except last */}
                      {index < detailFields.length - 1 && field.value && (
                        <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Action Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 2,
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                A confirmation has been sent to your email.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={onClose}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(138,33,29,0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(138,33,29,0.4)',
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default PopupEnquiryModal;